import filter from 'lodash/filter';
import flow from 'lodash/fp/flow';
import getOr from 'lodash/fp/getOr';
import pickBy from 'lodash/fp/pickBy';
import get from 'lodash/get';
import keys from 'lodash/keys';
import orderBy from 'lodash/orderBy';

import {LAYERS} from '@constants/mapbox/mapbox';

import horizonJSON from '@utils/json';

import {ROOT_RATINGS_CATEGORY_IDS} from '../../../../constants/categories';
import {VIEW_CITY_RATINGS, VIEW_COUNTRY_RATINGS, VIEW_PROVINCE_RATINGS} from '../../../../constants/permissions';
import {
  LOCATION_TIERS_ID,
  MAP_RATINGS_CATEGORY,
  MAP_RATINGS_LEVELS,
  MAP_RATINGS_ORGS,
  WORLD_MAP_RATINGS_FILTER_STORAGE_KEY_BASE,
} from '../../../../constants/rating-filters';
import {SORT_DIRECTION} from '../../../../constants/sorting';
import {getRatingKey} from '../../../../utils/rating-filters';
import {getCategoryName} from '../../categories/utils';

const ROOT_RATINGS_CATEGORY_VALUES = Object.values(ROOT_RATINGS_CATEGORY_IDS);

export const RATING_TYPES_VALUES = {
  COUNTRIES: 'COUNTRIES',
  PROVINCES: 'PROVINCES',
  CITIES: 'CITIES',
};

export const RATING_TYPES_MAPPING = [
  {id: 'countries', permissions: [VIEW_COUNTRY_RATINGS], value: RATING_TYPES_VALUES.COUNTRIES},
  {id: 'provinces', permissions: [VIEW_PROVINCE_RATINGS], value: RATING_TYPES_VALUES.PROVINCES},
  {id: 'cities', permissions: [VIEW_CITY_RATINGS], value: RATING_TYPES_VALUES.CITIES},
];

export function getRatingCurrentSelectedOrgPermissions(state, ratingsFilters) {
  const ratingOrg = ratingsFilters?.[MAP_RATINGS_ORGS];
  const fullPermissions = get(state, ['session', 'fullPermissions'], {});
  const permissions = fullPermissions[ratingOrg]?.permissions;
  return filter(keys(permissions), (key) => permissions[key] === true);
}

/**
 * From getState check the permissionIds and if it includes VIEW_PROVINCE_RATINGS
 * @param getState
 * @returns {boolean}
 */
export function checkForProvinceRatingPermission(getState) {
  const state = getState();
  const permissionIds = getRatingCurrentSelectedOrgPermissions(
    state,
    get(state, ['worldMap', WORLD_MAP_RATINGS_FILTER_STORAGE_KEY_BASE]),
  );
  return permissionIds.includes(VIEW_PROVINCE_RATINGS);
}

export function formRatingItemsIntoFeatures(ratingItems = [], store) {
  const currentCategoryId = get(store, ['worldMap', WORLD_MAP_RATINGS_FILTER_STORAGE_KEY_BASE, MAP_RATINGS_CATEGORY]);
  const ratingsDict = get(store, 'categories.dict', {});
  const ratingKey = getRatingKey(currentCategoryId);

  const features = ratingItems
    // this filter is needed only to be able to work fine in DEV/QA env
    // because some data maybe added to test and this data sometimes doesn't have geoJson
    .filter((ratingItem) => {
      return ratingItem?.geoJson;
    })
    .map((ratingItem) => {
      const feature = {
        type: 'Feature',
        properties: formRatingItemIntoFeatureProperties(ratingItem),
        geometry: horizonJSON.parse(ratingItem?.geoJson),
      };

      feature.properties.currentCategoryRating = get(feature, ['properties', ratingKey]);
      feature.properties.currentCategoryName = getCategoryName(ratingsDict, currentCategoryId);

      return feature;
    });

  return {type: 'FeatureCollection', features: features};
}

export function formRatingItemIntoFeatureProperties(ratingsItem, locale) {
  const localeKey = locale ? `_${locale}` : '';
  const allRatings = ratingsItem.ratings.reduce((accumulator, categoryRating) => {
    accumulator[getRatingKey(categoryRating.categoryId)] = categoryRating.rating ? categoryRating.rating : 0;
    accumulator[`color_${categoryRating.categoryId}`] = categoryRating.assessmentColor;
    accumulator[`assessment_${categoryRating.categoryId}${localeKey}`] = categoryRating.assessment;
    return accumulator;
  }, {});

  return {
    [`locationName${localeKey}`]: ratingsItem.location?.name,
    [`locationTier${localeKey}`]: ratingsItem.locationTier?.name,
    [`parentLocationName${localeKey}`]: ratingsItem.parentLocation?.name,
    countryId: ratingsItem.country?.id,
    locationId: ratingsItem.location?.id,
    id: ratingsItem.location?.id,
    ...allRatings,
  };
}

/**
 * Take feature object properties parse out the rating properties startsWith("rating_")
 * and parses those key via removing "rating_" then maps their values to rating objects
 *
 * {
 * rating: value,
 * categoryId: key,
 * name: getCategoryName(ratingsCategoriesDict, key)
 * }
 *
 * @param feature
 * @param ratingsCategoriesDict
 * @param locale
 * @returns {*}
 */
export function extractRankProperties(feature) {
  return flow([getOr({}, 'properties'), pickBy((value, key) => key.startsWith('rank_'))])(feature);
}

export function formRatingObjectFromFeatureRatings(feature, ratingsCategoriesDict, locale = 'en') {
  const ranks = extractRankProperties(feature);
  return flow([
    getOr({}, 'properties'),
    pickBy((value, key) => key.startsWith('rating_')),
    (ratingsByCategory) =>
      Object.entries(ratingsByCategory).map(([key, value]) => {
        const categoryId = key.replace('rating_', '');

        return {
          rank: ranks['rank_' + categoryId],
          rating: value,
          category: {
            id: categoryId,
            name: getCategoryName(ratingsCategoriesDict, categoryId),
          },
          parent: ROOT_RATINGS_CATEGORY_VALUES.includes(categoryId),
          ratingAssessment: feature?.properties?.[`assessment_${categoryId}_${locale}`],
          ratingAssessmentColor: feature?.properties?.[`color_${categoryId}`],
        };
      }),
  ])(feature);
}

export function parseRatingsPolygonFeatureResult(response, store, locale, ratingsFilters) {
  const ratingsData = response?.data?.content || [];
  const currentCategoryId = ratingsFilters?.[MAP_RATINGS_CATEGORY];
  const ratingsDict = get(store, 'categories.dict', {});

  const ratingLevels = ratingsFilters?.[MAP_RATINGS_LEVELS];

  const resCountryRatings = ratingsData
    .filter((feature) => {
      const currentRatingCategory = feature?.ratings?.find((ratingItem) => ratingItem.categoryId === currentCategoryId);
      const currentCategoryRating = currentRatingCategory?.rating || 0;
      return currentCategoryRating >= ratingLevels[0] && currentCategoryRating <= ratingLevels[1];
    })
    .map((feature) => {
      const locationTierId = feature?.locationTier?.id;
      let layerId;

      if (locationTierId === LOCATION_TIERS_ID.COUNTRY) {
        layerId = LAYERS.RATINGS_COUNTRY_LAYER_ID;
      } else if (locationTierId === LOCATION_TIERS_ID.PROVINCE) {
        layerId = LAYERS.RATINGS_PROVINCE_LAYER_ID;
      } else {
        layerId = LAYERS.RATINGS_CITY_LAYER_BOUNDARY_ID;
      }

      const currentRatingCategory = feature.ratings.find((ratingItem) => ratingItem.categoryId === currentCategoryId);
      const properties = formRatingItemIntoFeatureProperties(feature, locale);

      return {
        currentCategoryId,
        properties,
        layer: {id: layerId},
        currentCategoryRating: currentRatingCategory?.rating || 0,
        currentCategoryName: getCategoryName(ratingsDict, currentCategoryId),
        detailsRatings: formRatingObjectFromFeatureRatings({properties}, ratingsDict),
      };
    });

  return orderBy(
    resCountryRatings,
    ['locationTier.id', 'currentCategoryRating', 'location.name'],
    [SORT_DIRECTION.ASC, SORT_DIRECTION.DESC, SORT_DIRECTION.ASC],
  );
}

export function filterRatingTypesViaPermissions(hasPermission = {}, ratingTypes) {
  return RATING_TYPES_MAPPING.reduce((accumulator, item) => {
    const acceptedPermissions = item.permissions.filter((permission) => {
      return hasPermission[permission];
    });

    if (ratingTypes?.includes?.(item.value) && acceptedPermissions.length === item.permissions.length) {
      accumulator.push(item.value);
    }

    return accumulator;
  }, []);
}

/**
 * Returns true if RATINGS_PROVINCE_LAYER_ID matches the feature layer.id
 *
 * @param feature
 * @returns {*}
 */
export function isProvinceRatingFeature(feature) {
  const layerId = get(feature, 'layer.id');
  return layerId === LAYERS.RATINGS_PROVINCE_LAYER_ID;
}

/**
 * Returns true if RATINGS_COUNTRY_LAYER_ID or RATINGS_PROVINCE_LAYER_ID matches the feature layer.id
 *
 * @param feature
 * @returns {*}
 */
export function isRatingFeature(feature) {
  const layerId = get(feature, 'layer.id');
  return (
    layerId === LAYERS.RATINGS_COUNTRY_LAYER_ID ||
    layerId === LAYERS.RATINGS_PROVINCE_LAYER_ID ||
    layerId === LAYERS.RATINGS_CITY_LAYER_BOUNDARY_ID ||
    layerId === LAYERS.RATINGS_CITY_LAYER_POINT_ID
  );
}
